import { EmptyObject, createAction, createReducer } from '@reduxjs/toolkit';
import { graphql, readInlineData } from 'react-relay';
import { localStorage } from 'dibs-browser-storage';
import { wasPaymentDenied, wasPaymentRemoved } from '../helpers/checkoutForms';
import { trackPaneChange } from '../helpers/checkoutTracking';
import { isOfferPriceBookNameMismatch } from '../shipping/helpers/isOfferPriceBookNameMismatch';
import { PAYMENTS, PANES, STEPS, STORAGE, ERRORS, Pane } from '../constants/checkoutConstants';
import { getOfferPriceBookName } from './checkoutTransactionReducer';
import {
    checkoutPaneReducer_buyer$key,
    checkoutPaneReducer_buyer$data,
} from './__generated__/checkoutPaneReducer_buyer.graphql';
import {
    checkoutPaneReducer_preCheckoutTransaction$key,
    checkoutPaneReducer_preCheckoutTransaction$data,
} from './__generated__/checkoutPaneReducer_preCheckoutTransaction.graphql';

import {
    deliveryCompletedAction,
    newQuotesRedirectBuyerAction,
} from '../actions/checkoutDeliveryActions';

import {
    shipmentQuoteSelectedAction,
    navigateDeliveryAction,
    transactionBuyerChangedAction,
    shippingCompletedAction,
    setProcessingErrorAction,
    emptyShippingAddressAction,
    setQuantityAction,
    emptyPaymentMethodAction,
    saveCreditCardAction,
    saveBankAccountAction,
    savePaypalAction,
    saveCheckAction,
    saveWireTransferAction,
    saveOnPlatformWireTransferAction,
    saveApplePayAction,
    saveKlarnaPaymentMethodAction,
    completeCreditCardAction,
    completeBankAccountAction,
    navigateShippingAction,
    navigatePaymentAction,
    navigateReviewAction,
    submitKlarnaError,
    savePaymentMethod,
} from '../actions/commonActions';
import { setUpPanesAction } from '../actions/checkoutSetUpActions';

const paneList: string[] = [
    PANES.PANE_SIDEMARK,
    PANES.PANE_PROJECT,
    PANES.PANE_SHIPPING,
    PANES.PANE_DELIVERY,
    PANES.PANE_PAYMENT,
    PANES.PANE_REVIEW,
];

type StateAccessor<Args extends unknown[] = [], R = unknown> = (
    state: PaneState,
    ...args: Args
) => R;
type StateSetter<Args extends unknown[] = [], R = unknown> = StateAccessor<Args, R>;

// getters
export const getCurrentPane: StateAccessor<[], Pane> = state => state.currentPane;
export const isCurrentPane: StateAccessor<[Pane], boolean> = (state, pane) =>
    pane === getCurrentPane(state);
export const getInitialPane: StateAccessor<[], Pane | null> = state => state.initialPane;
export const getUpcomingPane: StateAccessor<[], Pane> = state => state.upcomingPane;
export const isPaneClickable: StateAccessor<[Pane], boolean> = (state, pane) => {
    /*
        TODO: This logic needs to be revisited. Temporary fix for ECOM-2803.
        `pane === undefined` was added in place of `PANES.PANE_Project === pane`
        which is basically the same thing, since `PANE_Project` is not a valid key.
    */
    //optional panes are always clickable
    if (PANES.PANE_SIDEMARK === pane || pane === undefined) {
        return true;
    }
    const currentPane = getCurrentPane(state);
    const isOnDifferentPane = currentPane !== pane;
    const isReviewPane = pane === PANES.PANE_REVIEW;

    return (
        !isReviewPane &&
        isOnDifferentPane &&
        paneList.includes(pane) &&
        paneList.indexOf(pane) <= paneList.indexOf(state.upcomingPane)
    );
};
export const isOnSidemarkPane: StateAccessor<[], boolean> = state => state.isOnSidemark;
export const isOnProjectPane: StateAccessor<[], boolean> = state => state.isOnProject;
export const isOnShippingPane: StateAccessor<[], boolean> = state =>
    isCurrentPane(state, PANES.PANE_SHIPPING);
export const isOnDeliveryPane: StateAccessor<[], boolean> = state =>
    isCurrentPane(state, PANES.PANE_DELIVERY);
export const isOnPaymentPane: StateAccessor<[], boolean> = state =>
    isCurrentPane(state, PANES.PANE_PAYMENT);
export const isOnReviewPane: StateAccessor<[], boolean> = state =>
    isCurrentPane(state, PANES.PANE_REVIEW);
export const isStepAccessible: StateAccessor<[Pane], boolean> = (state, step) => {
    return (
        paneList.includes(step) && paneList.indexOf(step) <= paneList.indexOf(state.upcomingPane)
    );
};

const updatePaneTracking: StateAccessor<[], void> = state => {
    const index = (Object.values(STEPS) as string[]).indexOf(state.currentPane);
    if (typeof index !== 'undefined') {
        trackPaneChange({
            step: index + 1,
            paneName: state.currentPane,
            product: state.pageViewTracking,
        });
    }
};

// setters
const setCurrentPane: StateSetter<[Pane, boolean?], void> = (state, pane, isRedirect = false) => {
    const paneIndex = paneList.indexOf(pane);
    const upcomingPaneIndex = paneList.indexOf(state.upcomingPane);

    const isDifferentPane = state.currentPane !== pane;
    state.currentPane = pane;
    state.upcomingPane = upcomingPaneIndex > paneIndex ? state.upcomingPane : pane;
    state.isOnSidemark = false;
    state.isOnProject = false;
    state.editSidemarkOnReview = false;
    state.editProjectOnReview = false;
    //Hold status of being redirected due to quantity changes to block off panes after redirection
    state.isRedirectedDueToPreQuotes = isRedirect;

    if (isDifferentPane) {
        updatePaneTracking(state);
    }
};
const completePayment: StateSetter<[], void> = state => {
    state.upcomingPane = PANES.PANE_REVIEW;
    state.currentPane = PANES.PANE_REVIEW;

    updatePaneTracking(state);
};

type PageViewTracking = {
    mobileAdaptive: boolean;
    id: string | null;
    name: string | null;
    brand: string | null;
    category: string | null;
    price: number | null;
    quantity: number | null;
};
export type PaneState = {
    isSetUp: boolean;
    currentPane: Pane;
    isOnSidemark: boolean;
    isOnProject: boolean;
    isSummaryOpen: boolean;
    editSidemarkOnReview: boolean;
    editProjectOnReview: boolean;
    pageViewTracking: PageViewTracking | EmptyObject;
    initialPane: Pane | null;
    upcomingPane: Pane;
    isRedirectedDueToPreQuotes: boolean;
    shippingAddressIdReference?: NonNullable<
        checkoutPaneReducer_buyer$data['firstValidShippingAddress']
    >['serviceId'];
    isReturningBuyer: boolean | null;
};

const initialState: PaneState = {
    isSetUp: false,
    currentPane: PANES.PANE_SHIPPING,
    isOnSidemark: false,
    isOnProject: false,
    isSummaryOpen: false,
    editSidemarkOnReview: false,
    editProjectOnReview: false,
    pageViewTracking: {},
    initialPane: null,
    upcomingPane: PANES.PANE_SHIPPING,
    isRedirectedDueToPreQuotes: false,
    isReturningBuyer: null,
};

type GetShippingComplete = (
    firstValidShippingAdress: checkoutPaneReducer_buyer$data['firstValidShippingAddress'],
    hasPartialShippingAddress: boolean,
    hasDefaultPartialAddress: boolean,
    addressPriceBookName: string | null | undefined,
    preCheckoutTransaction: checkoutPaneReducer_preCheckoutTransaction$data | null
) => boolean;

export const getIsShippingComplete: GetShippingComplete = (
    firstValidShippingAdress,
    hasPartialShippingAddress,
    hasDefaultPartialAddress,
    addressPriceBookName,
    preCheckoutTransaction
) => {
    const offerPriceBookName = getOfferPriceBookName({
        transaction: preCheckoutTransaction,
    });
    const { isAuctionOrderType, transactionType } = preCheckoutTransaction || {};
    const isAddressValidForOffer = !isOfferPriceBookNameMismatch(
        offerPriceBookName,
        addressPriceBookName || null,
        Boolean(isAuctionOrderType),
        transactionType || null
    );
    return (
        !!firstValidShippingAdress &&
        !hasPartialShippingAddress &&
        !hasDefaultPartialAddress &&
        isAddressValidForOffer
    );
};

export const editSideMarkAction = createAction('CHECKOUT_EDIT_SIDEMARK');
export const exitSideMarkAction = createAction('CHECKOUT_EXIT_SIDEMARK');
export const editProjectAction = createAction('CHECKOUT_PROJECT_EDIT');
export const exitProjectAction = createAction('CHECKOUT_PROJECT_EXIT');
export const toggleSummaryAction = createAction<{
    isSummaryOpen: boolean;
}>('CHECKOUT_TOGGLE_SUMMARY');
// trackPageViewAction should be moved to checkoutTrackingActions.ts once that is converted to TS
export const trackPageViewAction = createAction<{
    pageViewTracking: PageViewTracking;
}>('CHECKOUT_PAGEVIEW_TRACKING');

export const checkoutPaneReducer = createReducer(initialState, builder => {
    builder
        .addCase(setUpPanesAction, (state, action) => {
            const {
                buyer: buyerRef,
                deliveryOptions,
                preCheckoutTransaction: preCheckoutTransactionRef,
            } = action.payload;

            const buyer = readInlineData<checkoutPaneReducer_buyer$key>(
                graphql`
                    fragment checkoutPaneReducer_buyer on User @inline {
                        firstValidShippingAddress {
                            serviceId
                            priceBookName
                        }
                        addressBook {
                            personalAddress {
                                serviceId
                                validation {
                                    buyerShipping {
                                        valid
                                    }
                                }
                                priceBookName
                            }
                            addressList {
                                serviceId
                                validation {
                                    buyerShipping {
                                        valid
                                    }
                                }
                                priceBookName
                            }
                        }
                        wallet {
                            paymentMethods {
                                paymentMethod {
                                    __typename
                                    ... on CreditCard {
                                        isExpired
                                    }
                                }
                                shippingAddresses {
                                    serviceId
                                }
                            }
                        }
                    }
                `,
                buyerRef
            );

            const preCheckoutTransaction =
                readInlineData<checkoutPaneReducer_preCheckoutTransaction$key>(
                    graphql`
                        fragment checkoutPaneReducer_preCheckoutTransaction on Transaction @inline {
                            isWireTransfer
                            hasPartialShippingAddress
                            shipment {
                                shippingAddress {
                                    sourceId
                                }
                            }
                            isAuctionOrderType
                            transactionType
                            shipmentQuotes {
                                serviceId
                            }
                        }
                    `,
                    preCheckoutTransactionRef
                );

            const hasPartialShippingAddress = preCheckoutTransaction?.hasPartialShippingAddress;
            const preCheckoutAddressId =
                preCheckoutTransaction?.shipment?.shippingAddress?.sourceId;
            const { addressBook, firstValidShippingAddress } = buyer || {};
            const { personalAddress, addressList } = addressBook || {};
            const { serviceId: personalAddressId, validation: personalAddressValidation } =
                personalAddress || {};
            const addressListAddress = (addressList || []).find(address => {
                const { serviceId, validation } = address || {};
                return serviceId === preCheckoutAddressId && validation?.buyerShipping?.valid;
            });
            const shippingAddressId =
                addressListAddress?.serviceId ||
                personalAddressId ||
                firstValidShippingAddress?.serviceId;
            const walletPaymentMethods = buyer?.wallet?.paymentMethods || [];
            const hasDefaultCreditCard = !!walletPaymentMethods.find(obj => {
                const { paymentMethod, shippingAddresses } = obj || {};
                return (
                    shippingAddresses?.find(address => address?.serviceId === shippingAddressId) &&
                    paymentMethod?.__typename === PAYMENTS.TYPE_CREDIT_CARD &&
                    !paymentMethod?.isExpired
                );
            });
            const hasMultiplePreQuotes =
                (deliveryOptions || []).filter(q => !q.isLocalPickup).length > 1;
            const isWireTransfer = !!preCheckoutTransaction?.isWireTransfer;
            const hasPartialDefaultAddress =
                !personalAddressValidation?.buyerShipping?.valid &&
                shippingAddressId === personalAddressId;
            const addressPriceBookName =
                addressListAddress?.priceBookName ||
                personalAddress?.priceBookName ||
                firstValidShippingAddress?.priceBookName;
            const isShippingComplete =
                buyer &&
                getIsShippingComplete(
                    buyer?.firstValidShippingAddress,
                    !!hasPartialShippingAddress,
                    hasPartialDefaultAddress,
                    addressPriceBookName,
                    preCheckoutTransaction
                );
            const hasShipmentQuote = !!preCheckoutTransaction?.shipmentQuotes?.length;

            const isDeliveryComplete =
                isShippingComplete && !hasMultiplePreQuotes && hasShipmentQuote;
            const isPaymentComplete = hasDefaultCreditCard || isWireTransfer;
            const allStepsComplete = isShippingComplete && isDeliveryComplete && isPaymentComplete;
            let initialPane;
            let shippingAddressIdReference;

            const isReturningBuyer = isPaymentComplete && isShippingComplete;

            if (allStepsComplete || isReturningBuyer) {
                initialPane = PANES.PANE_REVIEW;
            } else if (isShippingComplete && isDeliveryComplete) {
                initialPane = PANES.PANE_PAYMENT;
            } else if (isShippingComplete) {
                initialPane = PANES.PANE_DELIVERY;
            } else {
                initialPane = PANES.PANE_SHIPPING;
            }
            if (isShippingComplete) {
                shippingAddressIdReference = buyer?.firstValidShippingAddress?.serviceId;
            }
            localStorage.setItem(STORAGE.SURVEY_INFO, {
                ...((localStorage.getItem(STORAGE.SURVEY_INFO) as Record<string, unknown>) || {}),
                step: initialPane,
            });

            state.isSetUp = true;
            state.isReturningBuyer = isReturningBuyer;
            state.initialPane = initialPane;
            state.currentPane = initialPane;
            state.upcomingPane = initialPane;
            state.shippingAddressIdReference = shippingAddressIdReference;
        })
        .addCase(trackPageViewAction, (state, action) => {
            const { pageViewTracking } = action.payload;
            state.pageViewTracking = pageViewTracking;
        })
        .addCase(transactionBuyerChangedAction, (state, action) => {
            const { isNewUser = false, address } = action.payload;

            state.isSetUp = isNewUser;
            state.currentPane = isNewUser ? PANES.PANE_DELIVERY : state.currentPane;
            state.upcomingPane = PANES.PANE_DELIVERY;
            state.shippingAddressIdReference = address?.serviceId;

            if (isNewUser) {
                updatePaneTracking(state);
            }
        })
        .addCase(editSideMarkAction, state => {
            if (state.currentPane === 'review') {
                state.isOnSidemark = true;
                state.editSidemarkOnReview = true;

                return;
            }

            state.isOnSidemark = true;
        })
        .addCase(exitSideMarkAction, state => {
            state.isOnSidemark = false;
        })
        .addCase(editProjectAction, state => {
            if (state.currentPane === 'review') {
                state.isOnProject = true;
                state.editProjectOnReview = true;

                return;
            }

            state.isOnProject = true;
        })
        .addCase(exitProjectAction, state => {
            state.isOnProject = false;
        })
        .addCase(toggleSummaryAction, (state, action) => {
            const { isSummaryOpen } = action.payload;
            state.isSummaryOpen = !isSummaryOpen;
        })
        .addCase(emptyShippingAddressAction, state => {
            state.currentPane = PANES.PANE_SHIPPING;
            state.upcomingPane = PANES.PANE_SHIPPING;
            state.shippingAddressIdReference = null;
        })
        .addCase(emptyPaymentMethodAction, state => {
            state.currentPane = PANES.PANE_PAYMENT;
            state.upcomingPane = PANES.PANE_PAYMENT;
        })
        .addCase(shippingCompletedAction, (state, action) => {
            const { shippingAddressId } = action.payload;
            const hasNewShippingAddress = state.shippingAddressIdReference !== shippingAddressId;
            const currentPane =
                hasNewShippingAddress || state.isRedirectedDueToPreQuotes
                    ? PANES.PANE_DELIVERY
                    : state.upcomingPane;

            state.currentPane = currentPane;
            state.upcomingPane = currentPane;
            state.shippingAddressIdReference = shippingAddressId;
            updatePaneTracking(state);
        })
        .addCase(deliveryCompletedAction, state => {
            const currentPane =
                paneList.indexOf(state.upcomingPane) > paneList.indexOf(PANES.PANE_PAYMENT)
                    ? state.upcomingPane
                    : PANES.PANE_PAYMENT;

            state.currentPane = currentPane;
            state.upcomingPane = currentPane;
            state.isRedirectedDueToPreQuotes = false;
            updatePaneTracking(state);
        })
        .addCase(setProcessingErrorAction, (state, action) => {
            const { errorStatus, errorMessage } = action.payload;
            const isPaymentDenied = wasPaymentDenied({ errorStatus, errorMessage });
            const isPaymentRemoved = wasPaymentRemoved({ errorStatus });
            if (isPaymentDenied || isPaymentRemoved) {
                state.currentPane = PANES.PANE_PAYMENT;
                return;
            }
            if (errorStatus === ERRORS.SHIPPING_ERROR) {
                state.currentPane = PANES.PANE_SHIPPING;
                return;
            }
        })
        .addCase(setQuantityAction, (state, action) => {
            const { quantity } = action.payload;
            state.pageViewTracking = { ...state.pageViewTracking, quantity };
        })
        .addCase(shipmentQuoteSelectedAction, (state, action) => {
            const { isCustomQuoteSelected } = action.payload;
            state.upcomingPane = isCustomQuoteSelected ? PANES.PANE_DELIVERY : state.upcomingPane;
        })
        .addCase(saveCreditCardAction, state => {
            completePayment(state);
        })
        .addCase(savePaymentMethod, state => {
            completePayment(state);
        })
        .addCase(saveBankAccountAction, state => {
            completePayment(state);
        })
        .addCase(savePaypalAction, state => {
            completePayment(state);
        })
        .addCase(saveCheckAction, state => {
            completePayment(state);
        })
        .addCase(saveWireTransferAction, state => {
            completePayment(state);
        })
        .addCase(saveOnPlatformWireTransferAction, state => {
            completePayment(state);
        })
        .addCase(saveApplePayAction, state => {
            completePayment(state);
        })
        .addCase(saveKlarnaPaymentMethodAction, state => {
            completePayment(state);
        })
        .addCase(completeCreditCardAction, state => {
            completePayment(state);
        })
        .addCase(completeBankAccountAction, state => {
            completePayment(state);
        })
        .addCase(navigateShippingAction, state => {
            setCurrentPane(state, PANES.PANE_SHIPPING);
        })
        .addCase(navigateDeliveryAction, state => {
            setCurrentPane(state, PANES.PANE_DELIVERY);
        })
        .addCase(navigatePaymentAction, state => {
            setCurrentPane(state, PANES.PANE_PAYMENT);
        })
        .addCase(navigateReviewAction, state => {
            setCurrentPane(state, PANES.PANE_REVIEW);
        })
        .addCase(submitKlarnaError, state => {
            state.currentPane = PANES.PANE_PAYMENT;
            state.upcomingPane = PANES.PANE_PAYMENT;
        })
        .addCase(newQuotesRedirectBuyerAction, (state, action) => {
            const { prevDeliveryOptions, newDeliveryOptions } = action.payload;
            const { currentPane } = state;

            //Check if deliveryOptions have changed to redirect buyer during checkout
            let haveDeliveryOptionsChanged = false;

            if (prevDeliveryOptions.length !== newDeliveryOptions.length) {
                //Compare lengths of prequotes to determine if available quotes changed
                haveDeliveryOptionsChanged = true;
            } else {
                //If the number of available quotes are the same, compare serviceIds to confirm if any have been removed or added
                newDeliveryOptions.forEach((q, i) => {
                    if (q?.serviceId !== prevDeliveryOptions[i]?.serviceId) {
                        haveDeliveryOptionsChanged = true;
                    }
                });
            }
            /**
             * Do not send user to the delivery step, since it's already in on the review step.
             */
            const returningBuyerTestCase = state.isReturningBuyer && currentPane === 'review';
            if (
                haveDeliveryOptionsChanged &&
                currentPane !== PANES.PANE_DELIVERY &&
                currentPane !== PANES.PANE_SHIPPING &&
                !returningBuyerTestCase
            ) {
                const isRedirect = true;
                setCurrentPane(state, PANES.PANE_DELIVERY, isRedirect);
                return;
            }
            if (haveDeliveryOptionsChanged && currentPane === PANES.PANE_SHIPPING) {
                state.isRedirectedDueToPreQuotes = true;
                return;
            }
        });
});
